import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Icon from '../../common/icons';
import annaAvatar from '../../../assets/Anna_Chat_Avatar.png';
import api, { trackCourseClick } from '../../../utils/api';
import {
  TYPES,
  MAX_PER_TYPE,
  rankTypes,
  clarityOf,
  strandKey,
  strandLabel,
  strandVerdict,
  strandFits,
  STRANDS,
  programsFor,
  rankStrandPrograms,
  courseTypes,
  fitOf,
  reasonOf,
  TIER_LABEL,
  normalizeName,
  searchCourses,
  courseStrands,
  codesReady,
  TOP_COUNT,
  catalogFor,
  fitForStudent,
  programOfSection,
  stageOf,
  strandsFor,
} from './riasecLogic';
import { fetchSavedPrograms, saveProgram, unsaveProgram } from './savedPrograms';
import ProgramIcon from '../../common/programIcons/ProgramIcon';
import { programLabel, twinOf } from './programCatalog';
import { otherSchoolsFor, postByCourses } from './otherSchools';
import { useSchoolOfferings } from './useSchoolOfferings';
import { isPhone, usePhone } from '../testShell/usePhone';
import './riasec.css';

const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const tx = (lang, en, fil) => (lang === 'fil' && fil ? fil : en);
const names = (types) => {
  const list = types.map((t) => TYPES[t].everyday);
  return list.length <= 1 ? list.join('') : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
};
const peso = (value) => (value == null || value === '' ? null : `₱${Number(value).toLocaleString('en-PH')}`);
const tuition = (row) => {
  const min = peso(row?.tuition_min);
  const max = peso(row?.tuition_max);
  if (min && max && min !== max) return `${min}–${max}`;
  return min || max || null;
};

const Bot = ({ children, ghost, wide }) => (
  <div className={`ts-row${wide ? ' is-wide' : ''}`}>
    <img className={`ts-av${ghost ? ' is-ghost' : ''}`} src={annaAvatar} alt="" aria-hidden="true" />
    <div className={`ts-stack${wide ? ' is-wide' : ''}`}>{children}</div>
  </div>
);
const Me = ({ children }) => (
  <div className="ts-row is-me">
    <div className="ts-stack">
      <div className="ts-bubble is-me">{children}</div>
    </div>
  </div>
);

const Badges = ({ row, strand, school, lang, saved }) => {
  const others = row.strands.filter((s) => s !== strand);
  return (
    <span className="rs-badges">
      {saved && (
        <span className="rs-badge is-saved">
          <Icon name="bookmark" size={12} />
          {tx(lang, 'Saved', 'Naka-save')}
        </span>
      )}
      {row.atSchool && (
        <span className="rs-badge is-school">
          <Icon name="check" size={12} />
          {school ? tx(lang, `At ${school}`, `Sa ${school}`) : tx(lang, 'Offered here', 'Inaalok dito')}
        </span>
      )}
      {strand && row.inStrand && <span className="rs-badge is-strand">{tx(lang, `Fits ${strandLabel(strand)}`, `Bagay sa ${strandLabel(strand)}`)}</span>}
      {strand && !row.inStrand && others.length > 0 && (
        <span className="rs-badge">{tx(lang, `Usually ${others.slice(0, 2).map(strandLabel).join(' or ')}`, `Kadalasan ${others.slice(0, 2).map(strandLabel).join(' o ')}`)}</span>
      )}
    </span>
  );
};

/**
 * RIASEC after a released result: the student's top interests (in the chat on a computer, on
 * their own screen on a phone), one line that says the test is saved and what comes next, then
 * on their tap a plain strand check and programs ranked by fit (Great / Good / Worth a look), one
 * program at a time beside the list (big window) or in a sheet with Previous / Next (phones,
 * corner window), "Search for a program", and the list they saved. Lives inside the test shell;
 * the shell passes the questions above it (history).
 */
const RiasecResult = ({ result, act, lang = 'en', school, context = {}, history, feedRef, testNote = null }) => {
  const scores = result.data?.scores || {};
  const top = result.data?.top || rankTypes(scores).slice(0, 3);
  const ranked = rankTypes(scores);
  const clarity = clarityOf(scores);
  const user = context.user || {};
  // The school's programs and whether they loaded ('loading' / 'ok' / 'failed', one retry): a
  // failed load says "Couldn't check …", never "Not at …"
  const { offerings, state: offeringsState } = useSchoolOfferings(context.offerings);
  // "Not at <school>" only means something when the school lists college programs at all
  const schoolHasPrograms = offeringsState === 'ok' && offerings.length > 0;
  // Grade 7-10: strands first; college: how the interests fit their program; else as before
  const level = stageOf(user);
  // The programs to recommend (riasecLogic.catalogFor: duplicates and tracks left out, this
  // school's own interest codes in place), the same list the Results page and the PDF use
  const { courses, offeredNames } = useMemo(
    () => catalogFor({ courses: result.data?.courses || [], offerings }),
    [result.data?.courses, offerings],
  );

  const phone = usePhone();
  const unclear = clarity === 'flat' || clarity === 'low';

  // result (Anna waits for the tap) → thinking → programs
  const [stage, setStage] = useState('result');
  // Phones: the result on its own screen over the chat: on (first time) → out (lifting) → off,
  // and 'again' when "See my full result" reopens it
  const [screen, setScreen] = useState(() => (isPhone() ? 'on' : 'off'));
  // The "Your top 3" strip, pinned while the result has scrolled out of view
  const [strip, setStrip] = useState(false);
  const resultRef = useRef(null);
  // The result block (Anna's line and the card) and the room under it that keeps the questions
  // above out of view while the student reads
  const introRef = useRef(null);
  const [spacer, setSpacer] = useState(0);
  const timers = useRef([]);
  const later = (fn, ms) => {
    timers.current.push(window.setTimeout(fn, ms));
  };
  useEffect(() => () => timers.current.forEach((id) => window.clearTimeout(id)), []);
  const [showMeaning, setShowMeaning] = useState(false);
  // Grade 7-10 haven't chosen a strand yet (a strand on file may be a placeholder), so Anna asks
  // which one they're thinking of; college students aren't asked at all
  const [strand, setStrand] = useState(level === 'senior' ? strandKey(user.strand) : null);
  const [strandAnswered, setStrandAnswered] = useState(level === 'college' || (level === 'senior' && Boolean(strandKey(user.strand))));
  // Whether Anna asks the strand question in this chat (the answer is shown back as a reply)
  const [askedStrand] = useState(!strandAnswered);
  // A strand picked here is saved to the profile and the new result (F10); juniors' pick is only
  // what they're thinking of, so it isn't saved
  const pickStrand = (key) => {
    setStrand(key);
    setStrandAnswered(true);
    if (!key || level === 'junior') return;
    const value = STRANDS.find((s) => s.key === key)?.value;
    if (value) api.put('/auth/user/', { strand: value }, { skipErrorToast: true }).catch(() => {});
  };
  const [filter, setFilter] = useState('all');
  const [tab, setTab] = useState('interests');
  const [exploreType, setExploreType] = useState(top[0]);
  // The list starts at the top few; "See … more" opens the rest six at a time
  const [shown, setShown] = useState(TOP_COUNT);
  // The open program: the rows Previous / Next walk through, which one is open, the direction of
  // the last step, the other schools that offer it (null while loading), and whether the full
  // description (desc) and every school (all) are shown
  const [detail, setDetail] = useState(null);
  // The big window shows the program beside the list; the corner window and phones use a sheet
  const [wide, setWide] = useState(false);
  const layoutRef = useRef(null);
  const touchRef = useRef(null);
  const [saved, setSaved] = useState([]);
  const [saveError, setSaveError] = useState('');
  // "Search for a program": its own sheet, with matches as the student types
  const [search, setSearch] = useState(null);
  const searchInput = useRef(null);

  const offeringFor = (course) => offerings.find((o) => normalizeName(o.course_name) === normalizeName(course.name));
  const verdict = strandVerdict({ top, strand, clarity });
  const programs = useMemo(
    () => programsFor({ courses: result.data?.courses || [], top, scores, strand, offerings }),
    [result.data?.courses, top, strand, offerings, scores],
  );
  // Grade 7-10: the strands that fit the top three, GAS as the open option
  const strandRows = useMemo(() => (level === 'junior' ? strandsFor({ top, scores, clarity }) : []), [level, top, scores, clarity]);
  // College: the program they're in, when their section names it
  const ownProgram = useMemo(() => (level === 'college' ? programOfSection(user, courses) : null), [level, user, courses]);
  const strandPrograms = useMemo(
    () => (strand ? rankStrandPrograms({ courses, top, strand, offeredNames }) : []),
    [courses, top, strand, offeredNames],
  );

  useEffect(() => {
    fetchSavedPrograms().then(setSaved).catch(() => {});
  }, []);

  // The student's tap brings the programs: Anna types for a beat, then the strand line and the list.
  // On a phone the result screen lifts away first; the chat underneath already has the small card.
  const goOn = () => {
    if (stage !== 'result') return;
    const fast = reducedMotion();
    const start = () => {
      setStage('thinking');
      later(() => setStage('programs'), fast ? 150 : 700);
    };
    if (phone && screen !== 'off') {
      setScreen('out');
      later(() => {
        setScreen('off');
        start();
      }, fast ? 0 : 260);
    } else {
      start();
    }
  };
  const reopenScreen = () => setScreen('again');
  const closeScreen = () => {
    setScreen('out');
    later(() => setScreen('off'), reducedMotion() ? 0 : 260);
  };

  // The result opens at the top of the view, instantly, with the questions scrolled out of the
  // way: room under the card lets the feed scroll that far
  useLayoutEffect(() => {
    const el = feedRef?.current;
    const block = introRef.current;
    if (!el || !block) return;
    setSpacer(Math.max(0, el.clientHeight - block.offsetHeight - 28));
  }, [wide]);
  useLayoutEffect(() => {
    const el = feedRef?.current;
    const block = introRef.current;
    if (!el || !block || stage !== 'result') return;
    const top = el.scrollTop + block.getBoundingClientRect().top - el.getBoundingClientRect().top - 10;
    el.scrollTop = Math.max(0, top);
  }, [spacer]);

  // After the tap: Anna's strand line and the programs come to the top (on a phone the small
  // "Your top 3" card stays above them); other steps bring the newest part into view
  const anchorRef = useRef(null);
  useEffect(() => {
    const el = feedRef?.current;
    if (!el) return undefined;
    const id = window.requestAnimationFrame(() => {
      const fast = reducedMotion();
      const target = phone ? resultRef.current : anchorRef.current;
      if (stage === 'programs' && target) {
        const top = el.scrollTop + target.getBoundingClientRect().top - el.getBoundingClientRect().top - 10;
        el.scrollTo({ top: Math.max(0, top), behavior: fast ? 'auto' : 'smooth' });
      } else if (stage !== 'result') {
        el.scrollTo({ top: el.scrollHeight, behavior: fast ? 'auto' : 'smooth' });
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [stage, strandAnswered]);

  // The search sheet: the field gets focus when it opens; Escape closes it (the open program first)
  useEffect(() => {
    if (!search) return undefined;
    searchInput.current?.focus({ preventScroll: true });
    const onKey = (event) => {
      if (event.key === 'Escape' && !detail) setSearch(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [Boolean(search), Boolean(detail)]);

  // The strip pins once the result (the card, or the small card on a phone) has scrolled up out of view
  useEffect(() => {
    const el = feedRef?.current;
    if (!el) return undefined;
    const check = () => {
      const card = resultRef.current;
      if (!card) return;
      const rect = card.getBoundingClientRect();
      setStrip(rect.height > 0 && rect.bottom <= el.getBoundingClientRect().top + 1);
    };
    el.addEventListener('scroll', check, { passive: true });
    check();
    return () => el.removeEventListener('scroll', check);
  }, []);

  const isSaved = (course) => saved.some((s) => normalizeName(s.course_name) === normalizeName(course.name));
  const toggleSave = async (row) => {
    setSaveError('');
    const course = row.course;
    try {
      if (isSaved(course)) {
        await unsaveProgram(course.name);
        setSaved((prev) => prev.filter((s) => normalizeName(s.course_name) !== normalizeName(course.name)));
      } else {
        const entry = await saveProgram({
          course_name: course.name,
          fit_label: row.tier ? TIER_LABEL[row.tier] : '',
          reason: reasonOf(row.matched || []),
        });
        setSaved((prev) => [...prev, entry]);
      }
    } catch {
      setSaveError(tx(lang, "Couldn't save that. Check your internet and try again.", 'Hindi na-save. Tingnan ang internet at subukan ulit.'));
    }
  };

  const sameCourse = (a, b) => normalizeName(a?.course?.name) === normalizeName(b?.course?.name);
  const current = detail ? detail.rows[detail.index] : null;
  const currentKey = current ? normalizeName(current.course.name) : null;

  /** Open one program; `rows` is the list the student is looking at, so Previous / Next walk it. */
  const openDetail = (row, rows = []) => {
    const list = rows.length ? rows : [row];
    const index = Math.max(0, list.findIndex((r) => sameCourse(r, row)));
    setSearch(null);
    setDetail({ rows: list, index, dir: 0, others: null, desc: false, all: false });
  };
  const closeDetail = () => setDetail(null);
  const stepDetail = (delta) =>
    setDetail((d) => {
      if (!d) return d;
      const index = d.index + delta;
      return index < 0 || index >= d.rows.length ? d : { ...d, index, dir: delta, others: null, desc: false, all: false };
    });

  // The other schools that offer the open program (the student's own school is shown on its own)
  useEffect(() => {
    if (!current) return undefined;
    const key = currentKey;
    const primary = current.matched?.[0] || courseTypes(current.course)[0] || null;
    trackCourseClick(current.course.name, 'riasec', null, primary);
    let live = true;
    const keep = (others) => setDetail((d) => (live && d && normalizeName(d.rows[d.index].course.name) === key ? { ...d, others } : d));
    // otherSchools.js reads the reply ({ results: { name: { schools } } }) the same way the
    // Results page and the PDF do: each school once, this school left out, Davao first
    postByCourses({ names: [current.course.name] }, { skipErrorToast: true })
      .then((response) => keep(otherSchoolsFor(response?.data, current.course.name, school)))
      .catch(() => keep([]));
    return () => {
      live = false;
    };
  }, [currentKey]);

  // Keep the open row in the list: rendered (past "Show more") and scrolled into view
  useEffect(() => {
    if (!detail) return;
    setShown((n) => Math.max(n, detail.index + 1));
    const el = feedRef?.current;
    if (!el) return;
    const row = Array.from(el.querySelectorAll('[data-row]')).find((node) => node.dataset.row === currentKey);
    if (row && typeof row.scrollIntoView === 'function') {
      const fast = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      row.scrollIntoView({ block: 'nearest', behavior: fast ? 'auto' : 'smooth' });
    }
  }, [detail?.index, currentKey]);

  // Arrow keys move between programs, Escape closes
  useEffect(() => {
    if (!detail) return undefined;
    const onKey = (event) => {
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (event.key === 'Escape') closeDetail();
      else if (event.key === 'ArrowRight') stepDetail(1);
      else if (event.key === 'ArrowLeft') stepDetail(-1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [Boolean(detail)]);

  // Big window (about 1100px wide): the program opens beside the list and stays open. Decided
  // before the first paint, so the result card is measured in its final shape below.
  useLayoutEffect(() => {
    const el = layoutRef.current;
    if (!el) return undefined;
    const check = () => setWide(el.clientWidth >= 860);
    check();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // A swipe left or right on the sheet moves between programs
  const onTouchStart = (event) => {
    const t = event.touches?.[0];
    touchRef.current = t ? { x: t.clientX, y: t.clientY } : null;
  };
  const onTouchEnd = (event) => {
    const start = touchRef.current;
    const t = event.changedTouches?.[0];
    touchRef.current = null;
    if (!start || !t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) > 56 && Math.abs(dy) < 40) stepDetail(dx < 0 ? 1 : -1);
  };

  // ---------- pieces ----------
  const clarityLine = {
    clear: { tone: 'good', icon: 'check', text: tx(lang, 'Your top interests stand out clearly.', 'Malinaw na nangunguna ang iyong mga interes.') },
    tied: { tone: 'good', icon: 'check', text: tx(lang, 'Your top three interests are about equally strong.', 'Halos magkasinlakas ang iyong tatlong nangungunang interes.') },
    flat: { tone: 'warn', icon: 'info', text: tx(lang, `Your answers were spread out evenly, so this result is less certain. ${TYPES[top[0]].everyday} is slightly ahead.`, `Pantay-pantay ang iyong mga sagot, kaya hindi pa ito tiyak. Bahagyang nangunguna ang ${TYPES[top[0]].everyday}.`) },
    low: { tone: 'warn', icon: 'info', text: tx(lang, 'You said yes to only a few things, so this result is less certain.', 'Kaunti lang ang sinagot mong oo, kaya hindi pa ito tiyak.') },
  }[clarity];

  // Which bars light up: the top three, or for a spread-out result just the one slightly ahead
  const lit = unclear ? (clarity === 'flat' ? [top[0]] : []) : top;
  const resultCard = (ref = null) => (
    <div className="ts-card rs-result" ref={ref}>
      <div className={`ts-card-body rs-result-body${unclear ? ' is-single' : ''}`}>
        {!unclear && (
        <div className="rs-result-main">
          <span className="ts-label">{tx(lang, 'Your top interests', 'Ang iyong mga nangungunang interes')}</span>
          {unclear ? null : (
            <ol className="rs-top">
              {top.map((t, i) => (
                <li key={t}>
                  <span className="rs-top-n">{i + 1}</span>
                  <span>
                    <b>
                      {TYPES[t].everyday}
                      <em>{TYPES[t].official}</em>
                    </b>
                    <small>{TYPES[t].meaning}</small>
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
        )}
        <div className="rs-result-side">
          {unclear ? (
            <span className="ts-label">{tx(lang, 'Your interests', 'Ang iyong mga interes')}</span>
          ) : (
            <span className="ts-label rs-all-label">{tx(lang, 'All six', 'Lahat ng anim')}</span>
          )}
          <div className="rs-bars" aria-label={tx(lang, 'All six interests', 'Lahat ng anim na interes')}>
            {ranked.map((t) => (
              <div className={`rs-bar${lit.includes(t) ? ' is-top' : ''}`} key={t}>
                <span>{TYPES[t].everyday}</span>
                <span className="rs-track">
                  <i style={{ width: `${Math.max(4, ((scores[t] || 0) / MAX_PER_TYPE) * 100)}%` }} />
                </span>
              </div>
            ))}
          </div>
          <p className={`rs-clarity is-${clarityLine.tone}`}>
            <Icon name={clarityLine.icon} size={16} />
            {clarityLine.text}
          </p>
          <button type="button" className="rs-link" aria-expanded={showMeaning} onClick={() => setShowMeaning((v) => !v)}>
            {showMeaning ? tx(lang, 'Hide what they mean', 'Itago ang ibig sabihin') : tx(lang, 'What do these interests mean?', 'Ano ang ibig sabihin ng mga ito?')}
          </button>
          {showMeaning && (
            <ul className="rs-meanings">
              {rankTypes(scores).map((t) => (
                <li key={t}>
                  <b>
                    {TYPES[t].everyday} <em>{TYPES[t].official}</em>
                  </b>
                  <span>{TYPES[t].meaning}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );

  // Phones: the small card that keeps the result in the chat once the screen has lifted
  const compactCard = (
    <div className="ts-card rs-compact" ref={resultRef}>
      <div className="ts-card-body">
        <span className="ts-label">{tx(lang, 'Your top 3', 'Ang top 3 mo')}</span>
        {unclear ? (
          <p className={`rs-clarity is-${clarityLine.tone}`}>
            <Icon name={clarityLine.icon} size={16} />
            {clarityLine.text}
          </p>
        ) : (
          <div className="rs-chips3">
            {top.map((t, i) => (
              <span key={t}>
                <i>{i + 1}</i>
                {TYPES[t].everyday}
              </span>
            ))}
          </div>
        )}
        <button type="button" className="rs-link" onClick={reopenScreen}>
          {tx(lang, 'See my full result', 'Tingnan ang buong resulta')}
        </button>
      </div>
    </div>
  );

  // Under the result: the test is saved, and what the button does
  const nextLine = level === 'college'
    ? tx(lang, "Your test is done and saved. Next, I'll show you how your interests fit your program.", 'Tapos na at naka-save ang test mo. Susunod, ipapakita ko kung paano bagay ang mga interes mo sa programa mo.')
    : unclear
    ? tx(lang, "Your test is done and saved. Your answers were spread out, so let's look at programs by interest.", 'Tapos na at naka-save ang test mo. Pantay-pantay ang mga sagot mo, kaya tingnan natin ang mga programa ayon sa interes.')
    : level === 'junior'
    ? tx(lang, "Your test is done and saved. Next, I'll show you the strands that fit you, then the programs they lead to.", 'Tapos na at naka-save ang test mo. Susunod, ipapakita ko ang mga strand na bagay sa iyo, at ang mga programang patungo roon.')
    : tx(lang, "Your test is done and saved. Next, I'll show you college programs that match these interests.", 'Tapos na at naka-save ang test mo. Susunod, ipapakita ko ang mga programa sa kolehiyo na bagay sa mga interes na ito.');
  const nextFoot = (
    <>
      <p className="rs-next">
        <Icon name="check" size={16} />
        {nextLine}
      </p>
      <button type="button" className="ts-btn is-dark" onClick={goOn}>
        <span>
          {level === 'college'
            ? tx(lang, 'Show me how it fits', 'Ipakita kung paano bagay')
            : unclear
            ? tx(lang, 'Show me programs by interest', 'Ipakita ang mga programa ayon sa interes')
            : level === 'junior'
            ? tx(lang, 'Show me the strands', 'Ipakita ang mga strand')
            : tx(lang, 'Show me the programs', 'Ipakita ang mga programa')}
        </span>
        <Icon name="arrow-right" size={18} />
      </button>
    </>
  );

  const strandText = () => {
    const label = strandLabel(strand);
    const fits = strandFits(strand);
    const inStrand = Array.isArray(fits) ? top.filter((t) => fits.includes(t)) : [];
    const outside = top.filter((t) => !inStrand.includes(t));
    switch (verdict) {
      case 'fits':
        return [tx(lang, `Your interests fit ${label} well. ${names(inStrand)} ${inStrand.length > 1 ? 'are' : 'is'} what ${label} is built on.`)];
      case 'partly':
        return [tx(lang, `Part of your interests fit ${label}: ${names(inStrand)}. Your ${names(outside)} ${outside.length > 1 ? 'sides point' : 'side points'} to other fields too, so here are both.`)];
      case 'diff':
        return [level === 'junior'
          ? tx(lang, `Your interests point a different way from ${label}. That's okay. The guidance office can help you weigh it before you choose.`)
          : tx(lang, `Your interests point a different way from ${label}. That's okay. Lots of people change direction for college.`)];
      case 'open':
        return [tx(lang, `${label} keeps your options open, so these are ranked by your interests.`)];
      case 'skip':
        return [tx(lang, "That happens, and it's useful to know. A counselor can help you narrow it down. Meanwhile, explore by interest:")];
      default:
        return [tx(lang, 'No problem. Here are programs ranked by your interests.')];
    }
  };

  // A plain render function (not a component), so rows aren't remounted on every render
  const programRow = (row, key, rows = [], rank = null) => (
    <button
      type="button"
      className={`rs-prog${current && sameCourse(current, row) ? ' is-open' : ''}`}
      key={key}
      data-row={normalizeName(row.course.name)}
      onClick={() => openDetail(row, rows)}
    >
      {rank && (
        <span className="rs-rank" aria-hidden="true">
          {rank}
        </span>
      )}
      <ProgramIcon name={row.course.name} size={40} />
      <span className="rs-prog-main">
        <b>{programLabel(row.course.name)}</b>
        {row.explore ? (
          row.course.category ? <small>{String(row.course.category)}</small> : null
        ) : (
          <small>
            {row.tier ? `${TIER_LABEL[row.tier]} · ` : ''}
            {row.tier
              ? reasonOf(row.matched)
              : isSaved(row.course)
              ? tx(lang, 'Saved from your chat', 'Na-save mo sa chat')
              : tx(lang, 'Not a strong match for your interests', 'Hindi gaanong tugma sa iyong interes')}
          </small>
        )}
        <Badges row={row} strand={strand} school={school} lang={lang} saved={isSaved(row.course)} />
      </span>
      <Icon name="chevron-right" size={18} />
    </button>
  );

  // The best few numbered, then the rest (once asked for) grouped by how well they fit
  const grouped = (rows) => {
    const first = rows.slice(0, TOP_COUNT);
    const rest = rows.slice(TOP_COUNT, Math.max(shown, TOP_COUNT));
    const groups = ['great', 'good', 'look'].map((tier) => ({ tier, rows: rest.filter((r) => r.tier === tier) })).filter((g) => g.rows.length);
    const left = rows.length - Math.max(shown, TOP_COUNT);
    return (
      <>
        <div className="rs-group">
          {first.map((row, i) => programRow(row, row.course._id || row.course.name, rows, i + 1))}
        </div>
        {groups.map((g) => (
          <div className="rs-group" key={g.tier}>
            <span className={`rs-group-title is-${g.tier}`}>{TIER_LABEL[g.tier]}</span>
            {g.rows.map((row) => (
              programRow(row, row.course._id || row.course.name, rows)
            ))}
          </div>
        ))}
        {left > 0 && (
          <button type="button" className="rs-more" onClick={() => setShown((n) => Math.max(n, TOP_COUNT) + 6)}>
            {shown <= TOP_COUNT
              ? tx(lang, `See ${left} more that ${left === 1 ? 'fits' : 'fit'}`, `Tingnan ang ${left} pang bagay`)
              : tx(lang, `Show ${Math.min(6, left)} more`, `Ipakita ang ${Math.min(6, left)} pa`)}
          </button>
        )}
      </>
    );
  };

  const programsCard = () => {
    if (!courses.length) {
      return (
        <div className="ts-card">
          <div className="ts-card-body">
            <p>{tx(lang, "I couldn't load the programs right now. Your result is saved, and your Results page lists programs for your interests.", 'Hindi ko ma-load ang mga programa ngayon. Naka-save ang iyong resulta.')}</p>
          </div>
        </div>
      );
    }
    if (verdict === 'skip') {
      // Spread-out answers: the student picks an interest and sees its programs
      const rows = courses
        .filter((c) => courseTypes(c).includes(exploreType))
        .map((course) => {
          const strands = courseStrands(course);
          return { course, ...fitOf(course, [exploreType]), tier: 'look', matched: [exploreType], strands, inStrand: Boolean(strand && strands.includes(strand)), atSchool: offeredNames.has(normalizeName(course.name)), explore: true };
        })
        .sort((a, b) => Number(b.atSchool) - Number(a.atSchool) || Number(b.inStrand) - Number(a.inStrand) || String(a.course.name).localeCompare(String(b.course.name)));
      const visible = rows.slice(0, shown);
      return (
        <div className="ts-card">
          <div className="ts-card-top rs-card-top">
            <span className="ts-tile">
              <Icon name="compass" size={20} />
            </span>
            <div>
              <h3 className="ts-card-title">{tx(lang, 'Explore by interest', 'Tuklasin ayon sa interes')}</h3>
              <span className="ts-card-sub">{tx(lang, 'Pick an interest to see its programs', 'Pumili ng interes para makita ang mga programa nito')}</span>
            </div>
          </div>
          <div className="ts-card-body">
            <div className="rs-filters" role="group" aria-label={tx(lang, 'Interests', 'Mga interes')}>
              {ranked.map((t) => (
                <button type="button" key={t} className={exploreType === t ? 'is-on' : ''} aria-pressed={exploreType === t} onClick={() => { setExploreType(t); setShown(TOP_COUNT); }}>
                  {TYPES[t].everyday}
                </button>
              ))}
            </div>
            <div className="rs-list">
              <div className="rs-group">
                <span className="rs-group-title is-good">
                  {TYPES[exploreType].everyday} · {rows.length} {tx(lang, rows.length === 1 ? 'program' : 'programs', 'programa')}
                </span>
                {visible.map((row) => programRow(row, row.course._id || row.course.name, rows))}
              </div>
              {rows.length > shown && (
                <button type="button" className="rs-more" onClick={() => setShown((n) => n + 6)}>
                  {tx(lang, `Show ${Math.min(6, rows.length - shown)} more`, `Ipakita ang ${Math.min(6, rows.length - shown)} pa`)}
                </button>
              )}
              {!rows.length && <p className="ts-note">{tx(lang, 'No programs are tagged with this interest yet.', 'Wala pang programang naka-tag sa interes na ito.')}</p>}
            </div>
          </div>
        </div>
      );
    }
    const strandOnly = verdict === 'diff' && tab === 'strand';
    let rows = strandOnly ? strandPrograms : programs;
    if (!strandOnly && filter === 'strand') rows = rows.filter((r) => r.inStrand);
    if (!strandOnly && filter === 'school') rows = rows.filter((r) => r.atSchool);
    const schoolCount = programs.filter((r) => r.atSchool).length;
    const strandCount = programs.filter((r) => r.inStrand).length;
    const topCount = Math.min(TOP_COUNT, programs.length);
    // With program codes only great matches count; the others only top the list up to five
    const fitCount = codesReady(courses) ? programs.filter((r) => r.tier === 'great').length : programs.length;
    return (
      <div className="ts-card">
        <div className="ts-card-top rs-card-top">
          <span className="ts-tile">
            <Icon name="graduation-cap" size={20} />
          </span>
          <div>
            <h3 className="ts-card-title">
              {topCount > 1
                ? tx(lang, `Your top ${topCount} programs`, `Ang iyong top ${topCount} na programa`)
                : tx(lang, 'Programs for you', 'Mga programa para sa iyo')}
            </h3>
            <span className="ts-card-sub">
              {fitCount >= topCount
                ? tx(lang, `${fitCount} fit your interests`, `${fitCount} ang bagay sa iyong interes`)
                : tx(lang, 'The closest programs to your interests', 'Ang pinakamalapit sa iyong interes')}
            </span>
          </div>
        </div>
        <div className="ts-card-body">
          {verdict === 'diff' && (
            <div className="rs-tabs" role="tablist">
              <button type="button" role="tab" aria-selected={tab === 'interests'} className={tab === 'interests' ? 'is-on' : ''} onClick={() => { setTab('interests'); setShown(TOP_COUNT); }}>
                {tx(lang, 'For my interests', 'Para sa interes ko')}
              </button>
              <button type="button" role="tab" aria-selected={tab === 'strand'} className={tab === 'strand' ? 'is-on' : ''} onClick={() => { setTab('strand'); setShown(TOP_COUNT); }}>
                {tx(lang, 'For my strand', 'Para sa strand ko')}
              </button>
            </div>
          )}
          {!strandOnly && (strandCount > 0 || schoolCount > 0) && (
            <div className="rs-filters" role="group" aria-label={tx(lang, 'Filter programs', 'I-filter ang mga programa')}>
              <button type="button" className={filter === 'all' ? 'is-on' : ''} aria-pressed={filter === 'all'} onClick={() => { setFilter('all'); setShown(TOP_COUNT); }}>
                {tx(lang, `All · ${programs.length}`)}
              </button>
              {strand && strandCount > 0 && (
                <button type="button" className={filter === 'strand' ? 'is-on' : ''} aria-pressed={filter === 'strand'} onClick={() => { setFilter('strand'); setShown(TOP_COUNT); }}>
                  {`${tx(lang, 'Fits', 'Bagay sa')} ${strandLabel(strand)} · ${strandCount}`}
                </button>
              )}
              {schoolCount > 0 && (
                <button type="button" className={filter === 'school' ? 'is-on' : ''} aria-pressed={filter === 'school'} onClick={() => { setFilter('school'); setShown(TOP_COUNT); }}>
                  {`${school ? tx(lang, `At ${school}`, `Sa ${school}`) : tx(lang, 'Offered here')} · ${schoolCount}`}
                </button>
              )}
            </div>
          )}
          <div className="rs-list">
            {strandOnly ? (
              <>
                {strandPrograms.slice(0, shown).map((row) => (
                  programRow(row, row.course._id || row.course.name, strandPrograms)
                ))}
                {strandPrograms.length > shown && (
                  <button type="button" className="rs-more" onClick={() => setShown((n) => n + 6)}>
                    {tx(lang, `Show ${Math.min(6, strandPrograms.length - shown)} more`)}
                  </button>
                )}
                {!strandPrograms.length && <p className="ts-note">{tx(lang, 'No programs are tagged with your strand yet.')}</p>}
              </>
            ) : rows.length ? (
              grouped(rows)
            ) : (
              <p className="ts-note">{tx(lang, 'Nothing here yet. Try another filter.', 'Wala pa rito. Subukan ang ibang filter.')}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // What the student typed, matched against the catalog as they type
  const searchRows = useMemo(() => {
    if (!search) return [];
    // Search every program so old names still find something ("Medical Technology" lands on
    // Medical Laboratory Science), then show the program the student would see
    const shown = new Map(courses.map((course) => [normalizeName(course.name), course]));
    const found = searchCourses(result.data?.courses || [], search.query, 12)
      .map((course) => shown.get(normalizeName(course.name)) || shown.get(normalizeName(twinOf(course.name))))
      .filter((course, i, list) => course && list.indexOf(course) === i)
      .slice(0, 8);
    return found.map((course) => {
      const strands = courseStrands(course);
      // The same fit the list gives it (riasecLogic.fitForStudent), so search never relabels a program
      const listed = programs.find((row) => normalizeName(row.course.name) === normalizeName(course.name));
      const fit = listed || fitForStudent(course, { top, scores, courses });
      return { course, score: fit.score, tier: fit.tier, matched: fit.matched, strands, inStrand: Boolean(strand && strands.includes(strand)), atSchool: offeredNames.has(normalizeName(course.name)) };
    });
  }, [search?.query, result.data?.courses, courses, top, strand, offeredNames, programs, scores]);

  const renderSearch = () => {
    if (!search) return null;
    const q = search.query;
    const typed = q.trim().length >= 2;
    return (
      <div className="rs-search-wrap" role="dialog" aria-modal="true" aria-label={tx(lang, 'Search for a program', 'Maghanap ng programa')}>
        <button type="button" className="rs-scrim" aria-label={tx(lang, 'Close', 'Isara')} onClick={() => setSearch(null)} />
        <div className="rs-search-sheet">
          <div className="rs-detail-head">
            <h3 className="rs-search-title">{tx(lang, 'Search for a program', 'Maghanap ng programa')}</h3>
            <button type="button" className="rs-close" aria-label={tx(lang, 'Close', 'Isara')} onClick={() => setSearch(null)}>
              <Icon name="x" size={18} />
            </button>
          </div>
          <div className="rs-search-body">
            <div className="rs-search">
              <Icon name="search" size={18} />
              <input
                ref={searchInput}
                value={q}
                onChange={(e) => setSearch({ query: e.target.value })}
                placeholder={tx(lang, 'A program or a job, like Nursing or Pilot', 'Programa o trabaho, gaya ng Nursing o Pilot')}
                aria-label={tx(lang, 'Program or job', 'Programa o trabaho')}
                maxLength={80}
                autoComplete="off"
              />
              {q && (
                <button type="button" className="rs-search-clear" aria-label={tx(lang, 'Clear', 'Burahin')} onClick={() => { setSearch({ query: '' }); searchInput.current?.focus(); }}>
                  <Icon name="x" size={16} />
                </button>
              )}
            </div>
            {!typed ? (
              <p className="ts-note">{tx(lang, 'Type a program name, or the job you have in mind.', 'I-type ang pangalan ng programa, o ang trabahong naiisip mo.')}</p>
            ) : searchRows.length ? (
              <div className="rs-list" aria-live="polite">
                {searchRows.map((row) => programRow(row, row.course._id || row.course.name, searchRows))}
              </div>
            ) : (
              <p className="ts-note">{tx(lang, `Nothing close to "${q.trim()}" yet. Try another word, like the job you want, or ask the guidance office.`, `Wala pang malapit sa "${q.trim()}". Subukan ang ibang salita, gaya ng trabahong gusto mo, o tanungin ang guidance office.`)}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ---------- program detail: beside the list in the big window, a sheet elsewhere ----------
  const renderDetail = () => {
    if (!detail || !current) return null;
    const row = current;
    const course = row.course;
    const here = offeringFor(course);
    const strands = row.strands?.length ? row.strands : courseStrands(course);
    const description = String(course.description || '').trim();
    const longText = description.length > 260;
    const total = detail.rows.length;
    const position = total > 1 ? tx(lang, `${detail.index + 1} of ${total}`, `${detail.index + 1} sa ${total}`) : '';
    const others = detail.others;
    const extra = others && others.length > 3 && !detail.all ? others.length - 3 : 0;
    const shownOthers = others ? (detail.all ? others : others.slice(0, 3)) : [];
    const facts = (s) => [s.duration_years ? `${s.duration_years} ${tx(lang, 'years', 'taon')}` : null, tuition(s)].filter(Boolean).join(' · ');
    const atStart = detail.index === 0;
    const atEnd = detail.index >= total - 1;
    return (
      <div className="rs-detail-wrap" role="dialog" aria-modal={wide ? undefined : 'true'} aria-label={programLabel(course.name)}>
        <button type="button" className="rs-scrim" aria-label={tx(lang, 'Close', 'Isara')} onClick={closeDetail} />
        <div className="rs-detail" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <div className="rs-detail-head">
            {row.tier && <span className={`rs-tier is-${row.tier}`}>{TIER_LABEL[row.tier]}</span>}
            {position && <span className="rs-pos">{position}</span>}
            <button type="button" className="rs-close" aria-label={tx(lang, 'Close', 'Isara')} onClick={closeDetail}>
              <Icon name="x" size={18} />
            </button>
          </div>
          <div className={`rs-detail-body${detail.dir > 0 ? ' is-fwd' : detail.dir < 0 ? ' is-back' : ''}`} key={currentKey}>
            <div className="rs-detail-title">
              <ProgramIcon name={course.name} size={56} />
              <h3>{programLabel(course.name)}</h3>
            </div>
            {row.tier && (
              <p className="rs-why">
                <b>{tx(lang, 'Why it fits:', 'Bakit bagay:')}</b> {reasonOf(row.matched).replace(/^Fits /, '')}.
              </p>
            )}
            {description && (
              <>
                <p className={`rs-desc${detail.desc || !longText ? ' is-open' : ''}`} data-keep-words="">
                  {description}
                </p>
                {longText && (
                  <button type="button" className="rs-link" aria-expanded={detail.desc} onClick={() => setDetail((d) => (d ? { ...d, desc: !d.desc } : d))}>
                    {detail.desc ? tx(lang, 'Show less', 'Paikliin') : tx(lang, 'Read more', 'Basahin pa')}
                  </button>
                )}
              </>
            )}
            {strands.length > 0 && (
              <>
                <span className="ts-label">{tx(lang, 'Strands that usually lead here', 'Mga strand na kadalasang patungo rito')}</span>
                <div className="rs-badges">
                  {strands.map((s) => (
                    <span key={s} className={`rs-badge${s === strand ? ' is-strand' : ''}`}>
                      {s === strand && <Icon name="check" size={12} />}
                      {strandLabel(s)}
                      {s === strand ? tx(lang, ' · yours', ' · iyo') : ''}
                    </span>
                  ))}
                </div>
                {strand && !strands.includes(strand) && (
                  <p className="ts-note">{tx(lang, 'Coming from another strand, some colleges may ask you to take bridging subjects first. Ask your counselor.', 'May mga kolehiyo na humihingi ng bridging subjects sa ibang strand. Tanungin ang iyong counselor.')}</p>
                )}
              </>
            )}
            <span className="ts-label">
              {offeringsState === 'ok' && !offerings.length
                ? tx(lang, 'Colleges that offer it', 'Mga kolehiyong nag-aalok nito')
                : tx(lang, 'Schools that offer it', 'Mga paaralang nag-aalok nito')}
              {!here && others && others.length > 0 ? ` · ${others.length}` : ''}
            </span>
            <div className="rs-where">
              {here ? (
                <div>
                  <Icon name="check" size={16} />
                  <span>{school ? tx(lang, `At ${school}`, `Sa ${school}`) : tx(lang, 'Offered here', 'Inaalok dito')}</span>
                  <small>{facts(here)}</small>
                </div>
              ) : (
                <>
                  {/* "Not at …" only when the school's list loaded and has college programs;
                      a failed load says so instead of calling every program missing */}
                  {offeringsState === 'failed' ? (
                    <div className="is-amber">
                      <Icon name="wifi-off" size={16} />
                      <span>{school ? tx(lang, `Couldn't check ${school} right now`, `Hindi ma-check ang ${school} ngayon`) : tx(lang, "Couldn't check your school's list right now", 'Hindi ma-check ang listahan ngayon')}</span>
                      <small />
                    </div>
                  ) : school && schoolHasPrograms ? (
                    <div className="is-muted">
                      <Icon name="info" size={16} />
                      <span>{tx(lang, `Not at ${school}`, `Wala sa ${school}`)}</span>
                      <small />
                    </div>
                  ) : null}
                  {others === null ? (
                    <div className="is-muted">
                      <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                      <span>{tx(lang, 'Checking other schools…', 'Tinitingnan ang ibang paaralan…')}</span>
                      <small />
                    </div>
                  ) : others.length ? (
                    shownOthers.map((s) => (
                      <div key={`${s.id}-${s.school_year || ''}`}>
                        <Icon name="building" size={16} />
                        <span>{s.school_name || s.name}</span>
                        <small>{facts(s)}</small>
                      </div>
                    ))
                  ) : (
                    <p className="ts-note">{tx(lang, 'No school has listed this program yet. The guidance office can help you find one.', 'Wala pang paaralang naglista ng programang ito. Makakatulong ang guidance office na mahanap ito.')}</p>
                  )}
                  {extra > 0 && (
                    <button type="button" className="rs-link" onClick={() => setDetail((d) => (d ? { ...d, all: true } : d))}>
                      {tx(lang, `Show ${extra} more`, `Ipakita ang ${extra} pa`)}
                    </button>
                  )}
                </>
              )}
            </div>
            {saveError && <p className="rs-error">{saveError}</p>}
          </div>
          <div className="rs-detail-foot">
            {total > 1 && (
              <div className="ts-btn-row rs-nav-row">
                <button type="button" className="ts-btn is-ghost is-prev" disabled={atStart} onClick={() => stepDetail(-1)}>
                  <Icon name="chevron-left" size={18} />
                  <span className="rs-nav-text">
                    <b>{tx(lang, 'Previous', 'Nakaraan')}</b>
                    <small>{atStart ? tx(lang, 'This is the first', 'Ito ang una') : programLabel(detail.rows[detail.index - 1].course.name)}</small>
                  </span>
                </button>
                <button type="button" className="ts-btn is-ghost is-next" disabled={atEnd} onClick={() => stepDetail(1)}>
                  <span className="rs-nav-text">
                    <b>{tx(lang, 'Next', 'Susunod')}</b>
                    <small>{atEnd ? tx(lang, 'This is the last', 'Ito ang huli') : programLabel(detail.rows[detail.index + 1].course.name)}</small>
                  </span>
                  <Icon name="chevron-right" size={18} />
                </button>
              </div>
            )}
            <div className="ts-btn-row">
              <button type="button" className="ts-btn is-dark" onClick={() => toggleSave(row)}>
                <Icon name="bookmark" size={18} />
                <span>{isSaved(course) ? tx(lang, 'Saved', 'Naka-save') : tx(lang, 'Save', 'I-save')}</span>
              </button>
              <button type="button" className="ts-btn is-ghost" onClick={() => act('counselor')}>
                {tx(lang, 'Book a counselor appointment', 'Mag-book ng appointment sa counselor')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ---------- Grade 7-10: strands first (N5) ----------
  const strandsCard = (
    <div className="ts-card rs-strands">
      <div className="ts-card-top rs-card-top">
        <span className="ts-tile">
          <Icon name="compass" size={20} />
        </span>
        <div>
          <h3 className="ts-card-title">{tx(lang, 'Strands that fit you', 'Mga strand na bagay sa iyo')}</h3>
          <span className="ts-card-sub">
            {unclear ? tx(lang, 'Your answers were spread out', 'Pantay-pantay ang mga sagot mo') : tx(lang, 'From your top interests', 'Mula sa iyong mga nangungunang interes')}
          </span>
        </div>
      </div>
      <div className="ts-card-body">
        <ul className="rs-strand-list">
          {strandRows.map((s) => (
            <li key={s.key} className={s.best ? 'is-best' : ''}>
              <span>
                <b>{s.label}</b>
                <small>{s.open ? tx(lang, 'Keeps your options open', 'Bukas pa ang mga pagpipilian mo') : s.matched.map((t) => TYPES[t].everyday).join(', ')}</small>
              </span>
              {s.best && <span className="rs-tier is-great">{tx(lang, 'Best fit', 'Pinakabagay')}</span>}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  // ---------- college: how the interests fit their program (N5) ----------
  const programFitCard = () => {
    const fit = ownProgram
      ? programs.find((row) => normalizeName(row.course.name) === normalizeName(ownProgram.name)) || fitForStudent(ownProgram, { top, scores, courses })
      : null;
    // The interests the program is built on, in the student's order (the same fit as the list)
    const matched = unclear || !fit ? [] : top.filter((t) => (fit.matched || []).includes(t));
    const grow = unclear ? [] : top.filter((t) => !matched.includes(t));
    const label = ownProgram ? programLabel(ownProgram.name) : '';
    return (
      <>
        <div className="ts-card rs-fitcard">
          <div className="ts-card-top rs-card-top">
            <span className="ts-tile">
              <Icon name="graduation-cap" size={20} />
            </span>
            <div>
              <h3 className="ts-card-title">{tx(lang, 'How your interests fit your program', 'Paano bagay ang interes mo sa programa mo')}</h3>
              {ownProgram && <span className="ts-card-sub">{label}</span>}
            </div>
          </div>
          <div className="ts-card-body">
            {ownProgram && fit ? (
              <>
                {matched.length > 0 ? (
                  <p>
                    <b>{label}</b> {tx(lang, `fits your ${names(matched)} ${matched.length > 1 ? 'sides' : 'side'} well.`, `ay bagay sa ${names(matched)} mo.`)}
                  </p>
                ) : (
                  <p>
                    <b>{label}</b> {tx(lang, `uses different strengths from your top interests (${names(top)}).`, `ay gumagamit ng ibang lakas kaysa sa ${names(top)} mo.`)}
                  </p>
                )}
                {matched.length > 0 && grow.length > 0 && (
                  <p className="ts-note">
                    {tx(lang, `Your ${names(grow.slice(0, 1))} side can grow here too, in projects, group work and activities outside class.`, `Puwede ring lumago rito ang ${names(grow.slice(0, 1))} mo, sa mga proyekto, group work at mga aktibidad.`)}
                  </p>
                )}
              </>
            ) : (
              <>
                <p>
                  {unclear
                    ? tx(lang, 'Your answers were spread out, so no single interest stands out yet.', 'Pantay-pantay ang mga sagot mo, kaya wala pang isang interes na nangunguna.')
                    : tx(lang, `Your top interests are ${names(top)}. Look up your program to see how it fits.`, `Ang mga nangungunang interes mo ay ${names(top)}. Hanapin ang programa mo para makita kung paano ito bagay.`)}
                </p>
                <button type="button" className="ts-btn is-ghost" onClick={() => setSearch({ query: '' })}>
                  <Icon name="search" size={18} />
                  <span>{tx(lang, 'Find my program', 'Hanapin ang programa ko')}</span>
                </button>
              </>
            )}
          </div>
        </div>
        <p className="rs-shift">
          <Icon name="info" size={16} />
          <span>{tx(lang, 'Thinking of shifting? Talk to the guidance office.', 'Iniisip mong lumipat ng programa? Kausapin ang guidance office.')}</span>
        </p>
      </>
    );
  };

  // ---------- feed ----------
  const showStrandQuestion = stage === 'programs' && !strandAnswered && !unclear;
  const counselorFirst = verdict === 'diff' || verdict === 'skip';
  const counselorLabel = tx(lang, 'Book a counselor appointment', 'Mag-book ng appointment sa counselor');
  const doneLabel = tx(lang, "I'm done for now", 'Tapos na ako sa ngayon');
  const scrollTop = () => feedRef?.current?.scrollTo({ top: 0, behavior: reducedMotion() ? 'auto' : 'smooth' });

  // Said with the result when the strand is known and the verdict reads on its own; the
  // "skip" and "none" lines lead into the list, so they stay with the programs
  const strandEarly = useRef(strandAnswered && !unclear && verdict !== 'none' && verdict !== 'skip').current;

  const resultScreen = phone && screen !== 'off' && (
    <section className={`rs-screen${screen === 'out' ? ' is-out' : ''}`} aria-label={tx(lang, 'Your result', 'Ang resulta mo')}>
      <div className="rs-screen-body">
        <div className="rs-screen-say">
          <img className="ts-av" src={annaAvatar} alt="" />
          <span>{tx(lang, "That's all 42. Here's what you enjoy most.", 'Iyan na ang lahat ng 42. Narito ang pinakagusto mo.')}</span>
        </div>
        {resultCard()}
      </div>
      <div className="rs-screen-foot">
        {screen === 'again' ? (
          <button type="button" className="ts-btn is-dark" onClick={closeScreen}>
            <Icon name="arrow-left" size={18} />
            <span>{tx(lang, 'Back to the programs', 'Balik sa mga programa')}</span>
          </button>
        ) : (
          nextFoot
        )}
      </div>
    </section>
  );

  const foot = (() => {
    if (stage === 'result') return phone ? null : <div className="ts-foot">{nextFoot}</div>;
    if (stage !== 'programs' || showStrandQuestion) return null;
    return (
      <div className="ts-foot">
        {counselorFirst ? (
          <button type="button" className="ts-btn is-dark" onClick={() => act('counselor')}>
            <Icon name="calendar" size={18} />
            <span>{counselorLabel}</span>
          </button>
        ) : (
          <button type="button" className="ts-btn is-dark" onClick={() => act('done')}>
            <Icon name="check" size={18} />
            <span>{doneLabel}</span>
          </button>
        )}
        <div className="ts-btn-row">
          <button type="button" className="ts-btn is-ghost" onClick={() => setSearch({ query: '' })}>
            <Icon name="search" size={18} />
            <span>{tx(lang, 'Search for a program', 'Maghanap ng programa')}</span>
          </button>
          {counselorFirst ? (
            <button type="button" className="ts-btn is-ghost" onClick={() => act('done')}>
              {doneLabel}
            </button>
          ) : (
            <button type="button" className="ts-btn is-ghost" onClick={() => act('counselor')}>
              <Icon name="calendar" size={18} />
              <span>{counselorLabel}</span>
            </button>
          )}
        </div>
      </div>
    );
  })();

  return (
    <div className={`rs-layout${wide ? ' is-wide' : ''}${detail ? ' has-detail' : ''}`} ref={layoutRef}>
      <div className="rs-main">
        {strip && stage === 'programs' && !unclear && (
          <button type="button" className="rs-strip" onClick={scrollTop}>
            <b>{tx(lang, 'Your top 3', 'Ang top 3 mo')}</b>
            {top.map((t, i) => (
              <span key={t} className="rs-strip-chip">
                <i>{i + 1}</i>
                {TYPES[t].everyday}
              </span>
            ))}
            <Icon name="chevron-up" size={16} />
          </button>
        )}
        <div className="ts-feed" ref={feedRef} aria-live="polite">
          {history}
          <div className="rs-result-block" ref={introRef}>
            <Bot>
              <div className="ts-bubble">
                {phone
                  ? tx(lang, "Here's what you enjoy most.", 'Narito ang pinakagusto mo.')
                  : tx(lang, "That's all 42. Here's what you enjoy most.", 'Iyan na ang lahat ng 42. Narito ang pinakagusto mo.')}
              </div>
            </Bot>
            <Bot ghost wide>
              {phone ? compactCard : resultCard(resultRef)}
            </Bot>
            {strandEarly &&
              strandText().map((text) => (
                <Bot key={text}>
                  <div className="ts-bubble">{text}</div>
                </Bot>
              ))}
          </div>
          {stage === 'result' && spacer > 0 && <div className="rs-spacer" style={{ height: spacer }} aria-hidden="true" />}

          <div ref={anchorRef} aria-hidden="true" />
          {stage === 'thinking' && (
            <Bot>
              <div className="ts-bubble is-typing" aria-label={tx(lang, 'Anna is typing', 'Nagta-type si Anna')}>
                <i />
                <i />
                <i />
              </div>
            </Bot>
          )}
          {stage === 'programs' && level === 'college' && (
            <>
              <Bot ghost wide>
                {programFitCard()}
              </Bot>
              {testNote && <Bot ghost wide>{testNote}</Bot>}
            </>
          )}
          {stage === 'programs' && level !== 'college' && (
            <>
              {level === 'junior' && (
                <Bot ghost wide>
                  {strandsCard}
                </Bot>
              )}
              {showStrandQuestion ? (
                <>
                  <Bot>
                    <div className="ts-bubble">
                      {level === 'junior'
                        ? tx(lang, 'Which strand are you thinking of?', 'Anong strand ang iniisip mo?')
                        : tx(lang, 'One quick question so I can match programs: which strand are you in?', 'Isang mabilis na tanong: anong strand mo?')}
                    </div>
                  </Bot>
                  <Bot ghost wide>
                    <div className="rs-chips">
                      {(level === 'junior' ? strandRows : STRANDS).map((s) => (
                        <button type="button" key={s.key} onClick={() => pickStrand(s.key)}>
                          {s.label}
                        </button>
                      ))}
                      <button type="button" className="is-skip" onClick={() => pickStrand(null)}>
                        {level === 'junior' ? tx(lang, 'Not sure yet', 'Hindi pa sigurado') : tx(lang, 'Skip', 'Laktawan')}
                      </button>
                    </div>
                  </Bot>
                </>
              ) : (
                <>
                  {askedStrand && strandAnswered && (
                    <Me>{strand ? strandLabel(strand) : level === 'junior' ? tx(lang, 'Not sure yet', 'Hindi pa sigurado') : tx(lang, 'Skip', 'Laktawan')}</Me>
                  )}
                  {!strandEarly &&
                    strandText().map((text) => (
                      <Bot key={text}>
                        <div className="ts-bubble">{text}</div>
                      </Bot>
                    ))}
                  <Bot ghost wide>
                    {programsCard()}
                  </Bot>
                  {/* Optional note for the guidance office (TestNoteBox, L7) */}
                  {testNote && <Bot ghost wide>{testNote}</Bot>}
                </>
              )}
            </>
          )}

        </div>
        {foot}
      </div>
      {renderSearch()}
      {renderDetail()}
      {resultScreen}
    </div>
  );
};

export default RiasecResult;
